import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, CheckCircle2, Clock, Flame, Building2, Plus, ArrowRight, Activity, Briefcase } from "lucide-react";
import { StatusPill, PriorityPill } from "@/components/StatusPill";
import { relTime } from "@/lib/format";

function StatCard({ label, value, icon: Icon, tone = "slate", testid }) {
  const tones = {
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    slate: "bg-slate-50 text-slate-700 border-slate-100",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-100",
  };
  return (
    <div data-testid={testid} className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 card-lift">
      <div className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="mt-3 font-display font-bold text-3xl sm:text-4xl text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function TroubleBadge({ score }) {
  if (score >= 6) return <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-700 border border-rose-100 px-2 py-0.5 text-[11px] font-semibold"><Flame className="w-3 h-3"/>High trouble</span>;
  if (score >= 3) return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 text-[11px] font-semibold"><AlertTriangle className="w-3 h-3"/>Watch</span>;
  return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 text-[11px] font-semibold"><CheckCircle2 className="w-3 h-3"/>Healthy</span>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [recent, setRecent] = useState([]);
  const [internalOpen, setInternalOpen] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [b, t] = await Promise.all([
          api.get("/dashboard/health-board"),
          api.get("/tickets"),
        ]);
        setBoard(b.data);
        setRecent(t.data.slice(0, 5));
        setInternalOpen(t.data.filter(x => x.type === "internal_job" && x.status !== "resolved").length);
      } catch (e) { /* handled by interceptor */ }
    })();
  }, []);

  const s = board?.stats || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-900">Hi, {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-slate-500 mt-1">Here's the state of your outlets right now.</p>
        </div>
        {user?.role !== "manager" && user?.role !== "op_manager" && (
          <button
            data-testid="new-ticket-btn"
            onClick={() => nav("/tickets/new")}
            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" /> New Ticket
          </button>
        )}
      </div>

      {/* Stats bento */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
        <div className="col-span-2 lg:col-span-2 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5 sm:p-6">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/10 px-2 py-0.5 text-[11px] font-semibold text-white/80">
            <Activity className="w-3 h-3" /> Health snapshot
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <div className="font-display font-bold text-5xl tabular-nums">{s.total ?? "—"}</div>
            <div className="text-sm text-white/70">total tickets</div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
            <div><div className="text-white/60 text-xs">Open</div><div className="font-semibold text-lg">{s.open ?? 0}</div></div>
            <div><div className="text-white/60 text-xs">In progress</div><div className="font-semibold text-lg">{s.in_progress ?? 0}</div></div>
            <div><div className="text-white/60 text-xs">Resolved</div><div className="font-semibold text-lg">{s.resolved ?? 0}</div></div>
          </div>
        </div>
        <StatCard testid="stat-overdue" label="Overdue" value={s.overdue ?? 0} icon={AlertTriangle} tone="rose" />
        <StatCard testid="stat-urgent" label="Urgent open" value={s.urgent_open ?? 0} icon={Flame} tone="amber" />
        <StatCard testid="stat-open" label="Open" value={s.open ?? 0} icon={Clock} tone="blue" />
        <StatCard testid="stat-resolved" label="Resolved" value={s.resolved ?? 0} icon={CheckCircle2} tone="emerald" />
      </div>

      {(user?.role === "facility_incharge" || user?.role === "admin") && internalOpen > 0 && (
        <button
          data-testid="internal-jobs-widget"
          onClick={() => nav("/tickets?type=internal_job")}
          className="w-full text-left rounded-2xl bg-white border border-indigo-100 p-5 card-lift flex items-center gap-4"
        >
          <div className="h-12 w-12 rounded-xl bg-indigo-50 text-indigo-600 grid place-items-center flex-shrink-0">
            <Briefcase className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-indigo-700">
              {user?.role === "facility_incharge" ? "My internal jobs" : "Internal jobs"}
            </div>
            <div className="mt-0.5 flex items-baseline gap-2">
              <div className="font-display font-bold text-3xl text-slate-900 tabular-nums">{internalOpen}</div>
              <div className="text-sm text-slate-500">still open</div>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {user?.role === "facility_incharge"
                ? "Admin-assigned back-office work — don't miss these while chasing outlet tickets."
                : "Back-office tasks currently open across the org."}
            </div>
          </div>
          <div className="text-xs font-semibold text-indigo-600 flex-shrink-0 hidden sm:block">View →</div>
        </button>
      )}

      {/* Outlet Health Board */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-xl text-slate-900">Outlet Health Board</h2>
          <span className="text-xs text-slate-500">Sorted by trouble level</span>
        </div>
        {!board ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0,1,2].map((i) => <div key={i} className="h-40 rounded-2xl bg-white border border-slate-200 animate-pulse" />)}
          </div>
        ) : board.outlets.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <Building2 className="w-8 h-8 text-slate-400 mx-auto" />
            <div className="mt-3 font-semibold text-slate-800">No outlets yet</div>
            <div className="text-sm text-slate-500">Add outlets from the Manage screen.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {board.outlets.map((o) => {
              const troublesome = o.overdue > 0 || o.urgent_open > 0;
              const isInternal = !!o.is_internal;
              return (
                <button
                  key={o.outlet_id}
                  data-testid={isInternal ? "internal-job-card" : `outlet-card-${o.outlet_code}`}
                  onClick={() => nav(isInternal ? "/tickets?type=internal_job" : `/tickets?outlet_id=${o.outlet_id}`)}
                  className={`text-left rounded-2xl border p-5 card-lift ${isInternal ? "border-indigo-300 bg-indigo-50/60" : troublesome ? "border-rose-200 bg-rose-50/30" : "border-slate-200 bg-white"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className={`text-[11px] font-semibold tracking-wide inline-flex items-center gap-1 ${isInternal ? "text-indigo-700" : "text-slate-500"}`}>
                        {isInternal && <Briefcase className="w-3 h-3" />} {o.outlet_code}
                      </div>
                      <div className="font-display font-bold text-lg text-slate-900 mt-0.5">{o.outlet_name}</div>
                    </div>
                    {isInternal ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200 px-2 py-0.5 text-[11px] font-semibold">
                        <Briefcase className="w-3 h-3" /> Admin-only
                      </span>
                    ) : (
                      <TroubleBadge score={o.trouble_score} />
                    )}
                  </div>
                  <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold text-slate-900 tabular-nums">{o.open}</div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Open</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-blue-700 tabular-nums">{o.in_progress}</div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Active</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-amber-700 tabular-nums">{o.overdue}</div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Overdue</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-emerald-700 tabular-nums">{o.resolved}</div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Done</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Recent tickets */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-xl text-slate-900">Latest tickets</h2>
          <button data-testid="see-all-tickets" onClick={() => nav("/tickets")} className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline">
            See all <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="space-y-2">
          {recent.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500 text-sm">No tickets yet.</div>
          ) : recent.map((t) => (
            <button
              key={t.id}
              data-testid={`recent-ticket-${t.id}`}
              onClick={() => nav(`/tickets/${t.id}`)}
              className={`w-full text-left rounded-xl border bg-white px-4 py-3 flex items-center justify-between gap-3 hover:border-indigo-300 transition-colors ${t.is_overdue ? "overdue-tint" : "border-slate-200"}`}
            >
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{t.title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{relTime(t.created_at)} • by {t.created_by_name}</div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <PriorityPill priority={t.priority} />
                <StatusPill status={t.status} overdue={t.is_overdue} />
              </div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
